import {Injectable} from '@angular/core';
import Order from '../models/Order';
import {interval, map, Observable, Observer} from 'rxjs';
import {v4 as uuidv4} from 'uuid';
import {Venue} from '../models/Venue';
import * as moment from 'moment';
import {TranslateService} from '@ngx-translate/core';
import {PreorderType} from '../models/PreorderType';

declare const epson: any;

interface PrinterStatus {
	battery: Number;
	code: string;
	printjobid: string;
	status: Number;
	success: boolean;
}

class PrinterHelper {
	constructor(
		private printer: any,
		private lineLength: number = 42
	) {}

	alignCenter() {
		this.printer.addTextAlign('center');
	}
	alignLeft() {
		this.printer.addTextAlign('left');
	}
	alignRight() {
		this.printer.addTextAlign('right');
	}

	println(text: string = '') {
		this.printer.addText(`${text}\n`);
	}

	spreadLn(left: string, right: string) {
		const textLength = left.length + right.length;
		let spacesToAdd: number;
		if (textLength < this.lineLength) {
			spacesToAdd = this.lineLength - textLength;
		} else {
			const remainder = textLength % this.lineLength;
			spacesToAdd = this.lineLength - remainder;
		}
		this.println(`${left}${' '.repeat(spacesToAdd)}${right}`);
	}

	setDoubleSize(width: boolean, height: boolean) {
		this.printer.addTextSize(width ? 2 : 1, height ? 2 : 1);
	}
	setBold(bold: boolean) {
		this.printer.addTextStyle(false, false, bold, undefined);
	}
	cut() {
		this.printer.addCut();
	}
	print(documentStatusObservable: Observable<PrinterStatus>): Promise<void> {
		return new Promise((resolve, reject) => {
			const uuid = uuidv4().split('-')[4];
			const subscription = documentStatusObservable.subscribe(status => {
				if (status.printjobid !== uuid) {
					return;
				}
				subscription.unsubscribe();
				if (status.success) {
					resolve();
				} else {
					reject(status);
				}
			});
			this.printer.send(uuid);
		});
	}
}

@Injectable({
	providedIn: 'root'
})
export class PrinterService {
	private printer: {connection: any; device: any};

	get isConnected(): boolean {
		return this.printer?.connection?.isConnected() === 1;
	}
	isConnectedObservable = interval(1_000).pipe(map(() => this.isConnected));
	private _statusObservable = new Observable((observer: Observer<any>) => {
		if (!this.isConnected) return;
		this.printer.device.startMonitor();

		this.printer.device.onerror = (error: any) => {
			observer.next(error);
		};

		this.printer.device.ononline = () => {
			observer.next('online');
		};

		this.printer.device.onoffline = () => {
			observer.next('offline');
		};

		this.printer.device.ondisconnect = () => {
			observer.next('disconnected');
		};
		this.printer.device.onstatuschange = (status: any) => {
			observer.next(status);
		};
	});

	get statusObservable(): Observable<any> {
		return this._statusObservable;
	}

	private _documentStatusObservable = new Observable(
		(observer: Observer<PrinterStatus>) => {
			if (!this.isConnected) return;
			this.printer.device.onreceive = (status: PrinterStatus) => {
				observer.next(status);
			};
		}
	);
	get documentStatusObservable(): Observable<any> {
		return this._documentStatusObservable;
	}

	constructor(public translate: TranslateService) {}

	async connect(
		ip: string,
		port: number = 8008,
		crypto: boolean = false
	): Promise<void> {
		localStorage.setItem('printerIp', ip);
		localStorage.setItem('printerPort', port.toString());
		localStorage.setItem('printerCrypto', crypto.toString());
		this.printer = await this.connectPrinter(ip, port, crypto);
	}

	async print(order: Order, venue: Venue): Promise<void> {
		console.log(`print(order: ${order}, venue: ${venue})`);
		if (!this.isConnected) {
			throw new Error('Printer is not initialized or connected');
		}
		const printer = new PrinterHelper(this.printer.device);
		printer.println();
		printer.println();
		printer.alignCenter();
		printer.println(venue.name);
		printer.println(`${venue.street} ${venue.number}`);
		printer.println(`${venue.postalCode} ${venue.city.de}`);
		printer.println();
		try {
			if (order?.preorder?.name) {
				printer.println(order?.preorder?.name);
			}
			if (order?.preorder?.phone) {
				printer.println(order?.preorder?.phone);
			}
			if (order?.preorder?.type) {
				printer.println(
					this.translate.instant('type.' + order?.preorder?.type)
				);
			}
			if (order?.preorder?.type === PreorderType.DELIVERY) {
				printer.println(
					order?.preorder?.street +
						' ' +
						order?.preorder?.number +
						', ' +
						order?.preorder?.postalCode +
						' ' +
						order?.preorder?.city
				);
			}
		} catch (e) {}

		printer.setBold(true);
		printer.println('Bestellnummer:');
		printer.setDoubleSize(true, true);
		printer.println(order.code);
		printer.println();
		printer.alignLeft();
		printer.setDoubleSize(false, false);
		printer.setBold(false);
		const orderAt = moment(order.orderAt);
		printer.spreadLn(
			`Datum: ${orderAt.format('dd.MM.yyyy')}`,
			`Uhrzeit: ${orderAt.format('HH:mm')}`
		);
		printer.println();
		printer.println();

		if (order) {
			order.orderedArticles.forEach(article => {
				printer.spreadLn(
					`${article.quantity}x ${article.article.name.de}`,
					`${article.article.price.$numberDecimal * article.quantity}`
				);
				article.groups.forEach(option => {
					printer.spreadLn(
						`  ${option.quantity}x ${option.article.name.de}`,
						`${option.article.price.$numberDecimal * option.quantity}`
					);
				});
			});
		}
		printer.println();
		printer.cut();
		return printer.print(this.documentStatusObservable);
	}

	private connectPrinter(
		ip: string,
		port: number,
		crypto: boolean = false
	): Promise<{connection: any; device: any}> {
		return new Promise((resolve, reject) => {
			const device = new epson.ePOSDevice();
			device.connect(ip, `${port}`, (result: string) => {
				if (result !== 'OK' && result !== 'SSL_CONNECT_OK') {
					reject(Error(`Connection failed with result ${result}`));
					return;
				}
				device.createDevice(
					'local_printer',
					device.DEVICE_TYPE_PRINTER,
					{crypto: crypto, buffer: false},
					(printer: any, code: string) => {
						if (code !== 'OK') {
							reject(Error(`Device created failed with code ${code}`));
							return;
						}
						resolve({
							connection: device,
							device: printer
						});
					}
				);
			});
		});
	}
}
